import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { saveTags } from "../../Store/slice/tagsSlice";

const PortalV2CreatableSelect = ({
  option,
  handleSelect,
  categoryId,
  label,
  value,
  showImportant = false,
  placeholder = "Select or type to create...",
}) => {
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState(option || []);
  const [creating, setCreating] = useState(false);
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (option) setOptions(option);
  }, [option]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  const fetchAllTags = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/tags`, {
        headers: { Authorization: `Bearer ${userData.token}` },
      });
      dispatch(saveTags(data.data));
    } catch (error) {
      console.log(error);
    }
  };

  const handleCreate = async (inputValue) => {
    if (!inputValue?.trim() || creating) return;
    setCreating(true);
    const newOption = {
      value: inputValue.toLowerCase(),
      label: inputValue,
      categoryId,
    };

    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/tags/create`,
        { categoryId: newOption.categoryId, name: newOption.value },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      newOption.id = data.tagId;
      setOptions((prev) => [...prev, newOption]);
      if (handleSelect) handleSelect(newOption);
      setOpen(false);
      fetchAllTags();
    } catch (error) {
      console.log(error);
    } finally {
      setCreating(false);
    }
  };

  const filtered = options.filter(
    (opt) => !search || opt.label.toLowerCase().includes(search.toLowerCase())
  );
  const canCreate =
    !!search.trim() &&
    !options.some((opt) => opt.label.toLowerCase() === search.trim().toLowerCase());

  const handleClear = (e) => {
    e.stopPropagation();
    if (handleSelect) handleSelect(null);
  };

  return (
    <div className="portal-v2-select-field" ref={rootRef}>
      {label ? (
        <span className="portal-v2-select-field__label">
          {label}
          {showImportant ? <em>*</em> : null}
        </span>
      ) : null}
      <div className={`portal-v2-select portal-v2-select--block ${open ? "is-open" : ""}`}>
        <button
          type="button"
          className="portal-v2-select__trigger portal-v2-select__trigger--block"
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={`portal-v2-select__value ${!value ? "is-placeholder" : ""}`}>
            {value?.label || placeholder}
          </span>
          <span className="portal-v2-select__trigger-end">
            {value ? (
              <button
                type="button"
                className="portal-v2-select__clear"
                onClick={handleClear}
                aria-label="Clear selection"
              >
                <CloseIcon />
              </button>
            ) : null}
            <KeyboardArrowDownOutlinedIcon className="portal-v2-select__chevron" />
          </span>
        </button>
        {open && (
          <div className="portal-v2-select__menu portal-v2-select__menu--block portal-v2-select__menu--creatable">
            <div className="portal-v2-select__search-wrap">
              <input
                ref={searchRef}
                type="text"
                className="portal-v2-select__search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search or type new..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canCreate) {
                    e.preventDefault();
                    handleCreate(search.trim());
                  }
                }}
              />
            </div>
            <ul className="portal-v2-select__options" role="listbox">
              {filtered.map((opt) => (
                <li key={opt.id || opt.value} role="option" aria-selected={value?.id === opt.id}>
                  <button
                    type="button"
                    className={`portal-v2-select__option ${value?.id === opt.id ? "is-selected" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (handleSelect) handleSelect(opt);
                      setOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
              {canCreate && (
                <li>
                  <button
                    type="button"
                    className="portal-v2-select__option portal-v2-select__option--create"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleCreate(search.trim())}
                    disabled={creating}
                  >
                    {creating ? "Creating..." : `Create "${search.trim()}"`}
                  </button>
                </li>
              )}
              {!filtered.length && !canCreate && (
                <li className="portal-v2-select__empty">No options found</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalV2CreatableSelect;
